import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import StatusBadge from "@/components/app/StatusBadge";
import CourierSelect from "@/components/app/CourierSelect";
import { CustomerEntitySelector, PARENT_ENTITY } from "@/components/app/CustomerEntitySelector";
import { PICKUP } from "@/constants/testIds";
import { Undo2, Plus, FileText, Loader2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const NEXT_STATUS = {
  scheduled: "picked_up",
  picked_up: "in_transit",
  in_transit: "received",
  received: "inspected",
  inspected: "closed",
};

// SLA hours per source status — matches the Exception Engine defaults so
// the queue colour maps 1:1 to `/admin/exceptions`.
const SLA_HOURS = { scheduled: 48, picked_up: 24, in_transit: 168, received: 24, inspected: 24 };

function pickupAgeHours(row) {
  const iso = row.status === "scheduled" ? row.created_at
    : row.status === "picked_up" ? (row.picked_up_at || row.created_at)
    : row.status === "in_transit" ? (row.picked_up_at || row.created_at)
    : row.status === "received" ? (row.received_at || row.updated_at)
    : row.updated_at || row.created_at;
  if (!iso) return 0;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return 0;
  return (Date.now() - t) / 3600_000;
}

function pickupSlaTone(row) {
  const sla = SLA_HOURS[row.status];
  if (!sla || !NEXT_STATUS[row.status]) return null;
  const age = pickupAgeHours(row);
  if (age > sla)         return { key: "red",   band: "border-l-red-500 bg-red-50/40",       chip: "bg-red-50 text-red-700 border-red-200",   label: `Overdue · ${age.toFixed(0)}h` };
  if (age > sla * 0.75)  return { key: "amber", band: "border-l-amber-400 bg-amber-50/40",   chip: "bg-amber-50 text-amber-700 border-amber-200", label: `${(sla-age).toFixed(0)}h left` };
  return                       { key: "green", band: "border-l-emerald-500",                 chip: "bg-emerald-50 text-emerald-700 border-emerald-200", label: `${age.toFixed(0)}h/${sla}h` };
}

export default function ReversePickup() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newOpen, setNewOpen] = useState(() => new URLSearchParams(window.location.search).get("new") === "1");
  const [employees, setEmployees] = useState([]);
  const [assets, setAssets] = useState([]);
  const [form, setForm] = useState({ master_company_id: "", master_company_name: "", subsidiary_id: "", subsidiary_name: "", employee_id: "", pickup_address: "", reason: "employee_offboarding", courier: "", awb: "", remarks: "" });
  const [selectedAssets, setSelectedAssets] = useState([]);
  const [showManual, setShowManual] = useState(false);
  const [manualQuery, setManualQuery] = useState("");
  const [manualAssets, setManualAssets] = useState([]);
  const [masterCompanies, setMasterCompanies] = useState([]);
  const [subCompanies, setSubCompanies] = useState([]);
  const [subsLoaded, setSubsLoaded] = useState(false);

  const [selected, setSelected] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/reverse-pickups", { params: { limit: 100 } })
      .then(({ data }) => setRows(data.items || []))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const toggleSel = (id) => setSelected((s) => {
    const n = new Set(s);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });

  const bulkAdvance = async () => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    // Group by "next status" to run one bulk call per next-state
    const byNext = {};
    for (const id of ids) {
      const row = rows.find((r) => r.id === id);
      const next = NEXT_STATUS[row?.status];
      if (!next) continue;
      byNext[next] = byNext[next] || [];
      byNext[next].push(id);
    }
    if (!Object.keys(byNext).length) {
      toast.error("None of the selected pickups have a next status.");
      return;
    }
    setBulkBusy(true);
    let ok = 0, total = 0;
    try {
      for (const [status, groupIds] of Object.entries(byNext)) {
        total += groupIds.length;
        try {
          const { data } = await api.post("/reverse-pickups/bulk/status", {
            ids: groupIds, status });
          ok += data.ok || 0;
        } catch (e) {
          toast.error(e.response?.data?.detail || `Bulk advance to ${status} failed`);
        }
      }
      toast.success(`${ok}/${total} pickup(s) advanced`);
      setSelected(new Set());
      load();
    } finally { setBulkBusy(false); }
  };

  useEffect(() => {
    api.get("/master-companies").then(({ data }) => setMasterCompanies(data.items || []));
  }, []);

  useEffect(() => {
    if (!form.master_company_id) { setSubCompanies([]); setSubsLoaded(false); return; }
    setSubsLoaded(false);
    api.get("/companies", { params: { master_company_id: form.master_company_id } })
      .then(({ data }) => setSubCompanies(data.items || data || []))
      .catch(() => setSubCompanies([]))
      .finally(() => setSubsLoaded(true));
  }, [form.master_company_id]);

  const noSubsidiaries = subsLoaded && subCompanies.length === 0;

  useEffect(() => {
    if (!form.master_company_id) return;
    const params = { master_company_id: form.master_company_id, limit: 100 };
    if (form.subsidiary_id) params.company_id = form.subsidiary_id;
    api.get("/employees", { params })
      .then(({ data }) => setEmployees(data.items || []));
  }, [form.master_company_id, form.subsidiary_id]);

  useEffect(() => {
    if (!form.employee_id) { setAssets([]); return; }
    api.get("/assets/", { params: { employee_id: form.employee_id, limit: 50 } })
      .then(({ data }) => {
        const items = data.items || [];
        setAssets(items);
        // Pre-select the employee's held assets so they are recovered by default.
        setSelectedAssets(items.map((a) => a.id));
      });
  }, [form.employee_id]);

  // Manual asset picker: search the company's assets to attach even when none
  // are auto-detected against the employee.
  useEffect(() => {
    if (!showManual || !form.master_company_id) { setManualAssets([]); return; }
    const t = setTimeout(() => {
      api.get("/assets/", { params: {
        master_company_id: form.master_company_id,
        company_id: form.subsidiary_id || undefined,
        q: manualQuery || undefined, limit: 50 } })
        .then(({ data }) => setManualAssets(data.items || []))
        .catch(() => setManualAssets([]));
    }, 250);
    return () => clearTimeout(t);
  }, [showManual, manualQuery, form.master_company_id, form.subsidiary_id]);

  const [dcBusy, setDcBusy] = useState("");
  const returnDc = async (row) => {
    setDcBusy(row.id);
    try {
      const { data } = await api.post(`/delivery-challans/from-pickup/${row.id}`);
      toast.success(`${data.existing ? "Return challan already exists" : "Return challan generated"} · ${data.code}`);
      window.open(`${API}/api/delivery-challans/${data.id}/pdf`, "_blank", "noopener");
      load();
    } catch (e) { const d = e.response?.data?.detail; toast.error(typeof d === "string" ? d : d?.message || "Could not generate the return challan"); }
    finally { setDcBusy(""); }
  };
  const advance = async (row) => {
    const next = NEXT_STATUS[row.status];
    if (!next) return;
    try {
      await api.post(`/reverse-pickups/${row.id}/status`, { status: next });
      toast.success(`Pickup ${next}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const create = async () => {
    try {
      await api.post("/reverse-pickups", {
        master_company_id: form.master_company_id,
        subsidiary_id: form.subsidiary_id || null,
        subsidiary_name: form.subsidiary_name || null,
        employee_id: form.employee_id,
        asset_ids: selectedAssets,
        pickup_address: form.pickup_address,
        reason: form.reason,
        courier: form.courier || null,
        awb: form.awb || null,
        remarks: form.remarks || null,
      });
      toast.success("Reverse pickup scheduled");
      setNewOpen(false);
      setSelectedAssets([]);
      setShowManual(false); setManualQuery(""); setManualAssets([]);
      setForm({ master_company_id: "", master_company_name: "", subsidiary_id: "", subsidiary_name: "", employee_id: "", pickup_address: "", reason: "employee_offboarding", courier: "", awb: "", remarks: "" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="space-y-4 max-w-[1600px]">
      <div className="flex justify-between items-end">
        <div>
          <div className="text-[10px] font-semibold tracking-[0.08em] uppercase text-slate-500">Operations</div>
          <h1 className="text-2xl font-heading font-semibold text-slate-900 tracking-tight">Reverse Pickups</h1>
          <p className="text-sm text-slate-500 mt-0.5">Recovery workflow from employee to warehouse.</p>
        </div>
        <Dialog open={newOpen} onOpenChange={setNewOpen}>
          <DialogTrigger asChild>
            <Button className="bg-slate-900 hover:bg-slate-800 text-white">
              <Plus className="h-4 w-4 mr-1.5" /> Schedule pickup
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg max-h-[90vh] flex flex-col">
            <DialogHeader><DialogTitle>Schedule Reverse Pickup</DialogTitle></DialogHeader>
            <div className="space-y-3 overflow-y-auto flex-1 min-h-0 pr-1 -mr-1">
              <div>
                <Label className="text-xs">Master Company</Label>
                <Select value={form.master_company_id} onValueChange={(v) => setForm({ ...form, master_company_id: v, master_company_name: (masterCompanies.find((m) => m.id === v) || {}).name || "", subsidiary_id: "", subsidiary_name: "", employee_id: "" })}>
                  <SelectTrigger className="h-10 mt-1"><SelectValue placeholder="Select…" /></SelectTrigger>
                  <SelectContent>
                    {masterCompanies.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {!noSubsidiaries && (
              <div>
                <Label className="text-xs">Subsidiary / Entity</Label>
                <div className="mt-1">
                  <CustomerEntitySelector
                    parentId={form.master_company_id} parentName={form.master_company_name}
                    value={form.subsidiary_id === "" && form.subsidiary_name === "" ? "" : (form.subsidiary_id || PARENT_ENTITY)}
                    valueLabel={form.subsidiary_name}
                    onChange={(v, item) => setForm((s) => ({
                      ...s,
                      subsidiary_id: (!v || v === PARENT_ENTITY) ? "" : v,
                      subsidiary_name: (!v || v === PARENT_ENTITY) ? "" : (item?.label || ""),
                      employee_id: "",
                    }))}
                    testId="pickup-subsidiary" />
                </div>
              </div>
              )}
              <div>
                <Label className="text-xs">Employee</Label>
                <Select value={form.employee_id} onValueChange={(v) => setForm({ ...form, employee_id: v })} disabled={!form.master_company_id}>
                  <SelectTrigger className="h-10 mt-1"><SelectValue placeholder="Select employee…" /></SelectTrigger>
                  <SelectContent className="max-h-72">
                    {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name} · {e.email}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {assets.length > 0 && (
                <div>
                  <Label className="text-xs">Assets to recover</Label>
                  <div className="mt-1 border border-slate-200 rounded-md divide-y divide-slate-100 max-h-40 overflow-y-auto">
                    {assets.map((a) => (
                      <label key={a.id} className="flex items-center gap-2 px-3 py-2 hover:bg-slate-50">
                        <input
                          type="checkbox"
                          checked={selectedAssets.includes(a.id)}
                          onChange={() => setSelectedAssets((s) => s.includes(a.id) ? s.filter((x) => x !== a.id) : [...s, a.id])}
                        />
                        <div className="text-sm">
                          <div>{a.brand} {a.model}</div>
                          <div className="text-xs text-slate-500 font-mono">{a.serial}</div>
                        </div>
                      </label>
                    ))}
                  </div>
                  <div className="text-[11px] text-slate-500 mt-1">
                    {selectedAssets.length} selected
                  </div>
                </div>
              )}
              {assets.length === 0 && form.employee_id && (
                <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-[12px] text-amber-900" data-testid="pickup-no-assets-hint">
                  No assets are currently assigned to this employee in the Golden Asset Record.
                  Use “Add assets” below to attach them manually, or schedule and link at inspection.
                </div>
              )}
              {form.master_company_id && (
                <div>
                  <button type="button" onClick={() => setShowManual((v) => !v)}
                    data-testid="pickup-add-assets-toggle"
                    className="inline-flex items-center gap-1.5 text-[13px] font-medium text-slate-700 hover:text-slate-900">
                    <Plus className="h-3.5 w-3.5" /> {showManual ? "Hide asset picker" : "Add assets"}
                  </button>
                  {showManual && (
                    <div className="mt-2 space-y-2">
                      <input
                        value={manualQuery}
                        onChange={(e) => setManualQuery(e.target.value)}
                        placeholder="Search by serial, asset tag, brand or model…"
                        data-testid="pickup-manual-asset-search"
                        className="w-full h-9 px-3 rounded-md border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-slate-300" />
                      <div className="border border-slate-200 rounded-md divide-y divide-slate-100 max-h-44 overflow-y-auto">
                        {manualAssets.filter((a) => !assets.some((x) => x.id === a.id)).map((a) => (
                          <label key={a.id} className="flex items-center gap-2 px-3 py-2 hover:bg-slate-50" data-testid={`pickup-manual-asset-${a.id}`}>
                            <input
                              type="checkbox"
                              checked={selectedAssets.includes(a.id)}
                              onChange={() => setSelectedAssets((s) => s.includes(a.id) ? s.filter((x) => x !== a.id) : [...s, a.id])}
                            />
                            <div className="text-sm min-w-0">
                              <div className="truncate">{a.brand} {a.model} {a.asset_tag ? `· ${a.asset_tag}` : ""}</div>
                              <div className="text-xs text-slate-500 font-mono truncate">
                                {a.serial}{a.current_employee_name ? ` · held by ${a.current_employee_name}` : ""}
                              </div>
                            </div>
                          </label>
                        ))}
                        {manualAssets.filter((a) => !assets.some((x) => x.id === a.id)).length === 0 && (
                          <div className="px-3 py-3 text-[12px] text-slate-400">No matching assets found.</div>
                        )}
                      </div>
                      <div className="text-[11px] text-slate-500">{selectedAssets.length} asset(s) selected for this pickup</div>
                    </div>
                  )}
                </div>
              )}
              <div>
                <Label className="text-xs">Pickup Address</Label>
                <Textarea value={form.pickup_address} onChange={(e) => setForm({ ...form, pickup_address: e.target.value })} className="mt-1" />
              </div>
              <div>
                <Label className="text-xs">Courier partner (optional)</Label>
                <CourierSelect value={form.courier}
                  onChange={(v) => setForm({ ...form, courier: v })}
                  testId="pickup-courier-select" />
              </div>
              <div>
                <Label className="text-xs">AWB number (optional)</Label>
                <Input value={form.awb} onChange={(e) => setForm({ ...form, awb: e.target.value })}
                  placeholder="Airway bill / tracking no." className="mt-1"
                  data-testid="pickup-awb-input" />
              </div>
              <div>
                <Label className="text-xs">Remarks (optional)</Label>
                <Textarea value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })}
                  placeholder="Any notes for this pickup…" className="mt-1"
                  data-testid="pickup-remarks-input" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setNewOpen(false)}>Cancel</Button>
              <Button
                className="bg-slate-900"
                disabled={!form.employee_id || !form.pickup_address?.trim() || (assets.length > 0 && selectedAssets.length === 0)}
                onClick={create}
                data-testid="pickup-schedule-btn"
              >
                Schedule
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        {selected.size > 0 && (
          <div className="bg-amber-50 border-b border-amber-200 px-3 py-2 flex items-center gap-2 text-[12px]"
                data-testid="pickup-bulk-bar">
            <span className="text-amber-900">
              {selected.size} pickup(s) selected
            </span>
            <Button size="sm" onClick={bulkAdvance} disabled={bulkBusy}
              className="ml-auto bg-amber-700 hover:bg-amber-800 text-white"
              data-testid="pickup-bulk-advance">
              <Undo2 className="h-3.5 w-3.5 mr-1" />
              Advance {selected.size} to next status
            </Button>
            <button type="button"
              onClick={() => setSelected(new Set())}
              data-testid="pickup-bulk-clear"
              className="text-[11px] underline underline-offset-2 text-amber-800">
              Clear
            </button>
          </div>
        )}
        <Table data-testid={PICKUP.table}>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead className="text-[10px] uppercase text-slate-500 w-8">
                <input type="checkbox"
                          onChange={(e) => {
                            if (e.target.checked)
                              setSelected(new Set(rows.filter((r) => NEXT_STATUS[r.status]).map((r) => r.id)));
                            else
                              setSelected(new Set());
                          }}
                          checked={selected.size > 0 && rows.filter((r) => NEXT_STATUS[r.status]).every((r) => selected.has(r.id))}
                          data-testid="pickup-bulk-select-all" />
              </TableHead>
              <TableHead className="text-[10px] uppercase text-slate-500">Pickup</TableHead>
              <TableHead className="text-[10px] uppercase text-slate-500">Client (Subsidiary)</TableHead>
              <TableHead className="text-[10px] uppercase text-slate-500">Employee</TableHead>
              <TableHead className="text-[10px] uppercase text-slate-500">Reason</TableHead>
              <TableHead className="text-[10px] uppercase text-slate-500">Assets</TableHead>
              <TableHead className="text-[10px] uppercase text-slate-500">Status</TableHead>
              <TableHead className="text-[10px] uppercase text-slate-500">SLA</TableHead>
              <TableHead className="text-[10px] uppercase text-slate-500">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={9} className="text-center py-8 text-sm text-slate-500">Loading…</TableCell></TableRow>
            ) : rows.length === 0 ? (
              <TableRow><TableCell colSpan={9} className="text-center py-8 text-sm text-slate-500">No pickups scheduled.</TableCell></TableRow>
            ) : rows.map((r) => {
              const sla = pickupSlaTone(r);
              return (
              <TableRow key={r.id}
                             data-testid={PICKUP.row(r.id)}
                             data-sla={sla?.key}
                             className={`border-l-4 ${sla?.band || "border-l-transparent"}`}>
                <TableCell>
                  {NEXT_STATUS[r.status] ? (
                    <input type="checkbox"
                              checked={selected.has(r.id)}
                              onChange={() => toggleSel(r.id)}
                              data-testid={`pickup-select-${r.id}`} />
                  ) : null}
                </TableCell>
                <TableCell className="font-mono text-xs">
                  <div>{r.pickup_code}</div>
                  {r.awb && (
                    <div className="text-[10px] text-slate-500 font-sans" data-testid={`pickup-awb-${r.id}`}>
                      AWB: {r.awb}
                    </div>
                  )}
                  {r.remarks && (
                    <div className="text-[10px] text-slate-400 font-sans italic truncate max-w-[200px]" title={r.remarks} data-testid={`pickup-remarks-${r.id}`}>
                      {r.remarks}
                    </div>
                  )}
                </TableCell>
                <TableCell className="text-sm" data-testid={`pickup-subsidiary-${r.id}`}>
                  {r.company_name ? (
                    <>
                      <div className="text-[13px] text-slate-900">{r.company_name}</div>
                      {r.master_company_name && (
                        <div className="text-[10px] text-slate-500">
                          {r.master_company_name}
                        </div>
                      )}
                    </>
                  ) : r.master_company_name ? (
                    <div className="text-[13px] text-slate-900">{r.master_company_name}</div>
                  ) : (
                    <span className="text-[11px] text-slate-400">—</span>
                  )}
                </TableCell>
                <TableCell className="text-sm" data-testid={`pickup-employee-${r.id}`}>
                  {r.employee_name ? (
                    <>
                      <div className="text-[13px] text-slate-900">{r.employee_name}</div>
                      {r.employee_email && (
                        <div className="text-[10px] text-slate-500 truncate max-w-[220px]">
                          {r.employee_email}
                        </div>
                      )}
                    </>
                  ) : (
                    <span className="text-[11px] text-slate-400">—</span>
                  )}
                </TableCell>
                <TableCell className="text-sm capitalize">{r.reason?.replace(/_/g, " ")}</TableCell>
                <TableCell className="text-sm">{r.asset_ids?.length || 0}</TableCell>
                <TableCell><StatusBadge value={r.status} /></TableCell>
                <TableCell>
                  {sla ? (
                    <span className={`inline-flex items-center text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded border ${sla.chip}`}
                            data-testid={`pickup-sla-${r.id}`}>
                      {sla.label}
                    </span>
                  ) : <span className="text-[10px] text-slate-400">—</span>}
                </TableCell>
                <TableCell>
                  <div className="flex flex-wrap items-center gap-1.5">
                    {r.dc_code ? (
                      <a href={`${API}/api/delivery-challans/${r.dc_id}/pdf`} target="_blank" rel="noreferrer" data-testid={`pickup-dc-pdf-${r.id}`}
                        className="inline-flex h-8 items-center gap-1 rounded-md border border-emerald-300 bg-emerald-50 px-2 text-[11px] font-mono font-semibold text-emerald-800 hover:bg-emerald-100">
                        <FileText className="h-3.5 w-3.5" /> {r.dc_code}
                      </a>
                    ) : !["cancelled", "closed"].includes(r.status) && (
                      <Button size="sm" variant="outline" disabled={dcBusy === r.id || !(r.asset_ids?.length)} onClick={() => returnDc(r)} data-testid={`pickup-return-dc-${r.id}`} title={r.asset_ids?.length ? "Generate the Return Delivery Challan (one click)" : "Add devices to the pickup first"}>
                        {dcBusy === r.id ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <FileText className="h-4 w-4 mr-1.5" strokeWidth={1.5} />} Return DC
                      </Button>
                    )}
                    {NEXT_STATUS[r.status] ? (
                      <Button data-testid={PICKUP.statusBtn} size="sm" variant="outline" onClick={() => advance(r)}>
                        <Undo2 className="h-4 w-4 mr-1.5" strokeWidth={1.5} />
                        Mark {NEXT_STATUS[r.status].replace(/_/g, " ")}
                      </Button>
                    ) : null}
                  </div>
                </TableCell>
              </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
